import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  EyeIcon, 
  Squares2X2Icon,
  UsersIcon,
  UserIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline';
import { organizationAPI, departmentsAPI as departmentAPI, employeeAPI } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';

const DivisionManagement = () => {
  const [divisions, setDivisions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadDivisions();
    }
  }, [selectedCompany, selectedDepartment]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [companiesResponse, departmentsResponse, employeesResponse] = await Promise.all([
        organizationAPI.getCompanies(),
        departmentAPI.getAll(),
        employeeAPI.getAll({ employment_status: 'active' })
      ]);

      if (companiesResponse.data.success) {
        setCompanies(companiesResponse.data.data);
        if (companiesResponse.data.data.length > 0 && !selectedCompany) {
          setSelectedCompany(companiesResponse.data.data[0].id);
        }
      }

      if (departmentsResponse.data.departments) {
        setDepartments(departmentsResponse.data.departments);
      }

      if (employeesResponse.data.success) {
        setEmployees(employeesResponse.data.data);
      }

      await loadDivisions();
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const loadDivisions = async () => {
    try {
      const params = {};
      if (selectedCompany) params.company_id = selectedCompany;
      if (selectedDepartment) params.department_id = selectedDepartment;
      
      const response = await organizationAPI.getDivisions(params);
      if (response.data.success) {
        setDivisions(response.data.data);
      }
    } catch (err) {
      console.error('Error loading divisions:', err);
    }
  };

  const handleCreateDivision = () => {
    setSelectedDivision(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleEditDivision = (division) => {
    setSelectedDivision(division);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleViewDivision = (division) => {
    setSelectedDivision(division);
    setModalMode('view');
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedDivision(null);
  };

  const handleSave = async (formData) => {
    try {
      if (modalMode === 'create') {
        await organizationAPI.createDivision(formData);
      } else if (modalMode === 'edit') {
        await organizationAPI.updateDivision(selectedDivision.id, formData);
      }
      await loadDivisions();
      handleModalClose();
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Error saving division');
    }
  };

  // Filter departments by selected company
  const filteredDepartments = departments.filter(dept => 
    !selectedCompany || dept.company_id === selectedCompany
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Division Management</h1>
          <p className="text-gray-600">Manage divisions within departments</p>
        </div>
        <div className="flex items-center space-x-4">
          {companies.length > 1 && (
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Companies</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          )}
          {filteredDepartments.length > 1 && (
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Departments</option>
              {filteredDepartments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={handleCreateDivision}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Division
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {divisions.map((division) => {
          const company = companies.find(c => c.id === division.company_id);
          const department = departments.find(d => d.id === division.department_id);
          const manager = employees.find(e => e.id === division.division_manager_id);
          const divisionEmployees = employees.filter(e => e.division_id === division.id);

          return (
            <div key={division.id} className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Squares2X2Icon className="h-10 w-10 text-primary-600" />
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{division.name}</h3>
                    <p className="text-sm text-gray-500">Code: {division.code || 'N/A'}</p>
                    {department && (
                      <div className="flex items-center text-sm text-gray-500">
                        <BuildingOffice2Icon className="h-4 w-4 mr-1" />
                        {department.name}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="flex items-center justify-center">
                        <UsersIcon className="h-5 w-5 text-gray-400 mr-1" />
                        <span className="text-2xl font-bold text-gray-900">{divisionEmployees.length}</span>
                      </div>
                      <p className="text-sm text-gray-500">Employees</p>
                    </div>
                    <div>
                      <div className="flex items-center justify-center">
                        <span className={`text-2xl font-bold ${division.is_active ? 'text-green-600' : 'text-red-600'}`}>
                          {division.is_active ? '●' : '○'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {division.is_active ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                  </div>
                </div>

                {manager && (
                  <div className="mt-4 flex items-center text-sm text-gray-600">
                    <UserIcon className="h-4 w-4 mr-1" />
                    Manager: {manager.first_name} {manager.last_name}
                  </div>
                )}

                {division.location && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">📍 {division.location}</p>
                  </div>
                )}

                {division.is_profit_center && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Profit Center
                    </span>
                  </div>
                )}

                {division.description && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 line-clamp-2">{division.description}</p>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-2">
                <button
                  onClick={() => handleViewDivision(division)}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  View
                </button>
                <button
                  onClick={() => handleEditDivision(division)}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {divisions.length === 0 && (
        <div className="text-center py-12">
          <Squares2X2Icon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No divisions</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new division.</p>
          <div className="mt-6">
            <button
              onClick={handleCreateDivision}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Add Division
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <DivisionModal
          mode={modalMode}
          division={selectedDivision}
          companies={companies}
          departments={filteredDepartments}
          employees={employees}
          onClose={handleModalClose}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

const DivisionModal = ({ mode, division, companies, departments, employees, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    company_id: companies.length > 0 ? companies[0].id : '',
    department_id: '',
    division_manager_id: '',
    parent_division_id: '',
    location: '',
    cost_center: '',
    budget_amount: '',
    budget_year: new Date().getFullYear(),
    annual_goals: '',
    performance_target: '',
    is_profit_center: false,
    is_active: true,
    ...division
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'view') return;

    try {
      setLoading(true);
      setError('');
      await onSave(formData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isReadOnly = mode === 'view';
  const title = mode === 'create' ? 'Add New Division' : mode === 'edit' ? 'Edit Division' : 'Division Details';

  // Filter employees for manager selection
  const managerOptions = employees.filter(emp => emp.employment_status === 'active');
  
  // Filter departments by selected company
  const filteredDepartments = departments.filter(dept => 
    !formData.company_id || dept.company_id === formData.company_id
  );

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 border-b pb-2">Basic Information</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Division Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  readOnly={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Division Code</label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  readOnly={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Company *</label>
                <select
                  name="company_id"
                  value={formData.company_id}
                  onChange={handleChange}
                  required
                  disabled={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select Company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Department *</label>
                <select
                  name="department_id"
                  value={formData.department_id}
                  onChange={handleChange}
                  required
                  disabled={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select Department</option>
                  {filteredDepartments.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Division Manager</label>
                <select
                  name="division_manager_id"
                  value={formData.division_manager_id}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select Manager</option>
                  {managerOptions.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.first_name} {employee.last_name} - {employee.job_title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Location</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  readOnly={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Financial & Performance */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 border-b pb-2">Financial & Performance</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Cost Center</label>
                <input
                  type="text"
                  name="cost_center"
                  value={formData.cost_center}
                  onChange={handleChange}
                  readOnly={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Budget Amount</label>
                  <input
                    type="number"
                    name="budget_amount"
                    value={formData.budget_amount}
                    onChange={handleChange}
                    step="0.01"
                    readOnly={isReadOnly}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Budget Year</label>
                  <input
                    type="number"
                    name="budget_year"
                    value={formData.budget_year}
                    onChange={handleChange}
                    readOnly={isReadOnly}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Performance Target (%)</label>
                <input
                  type="number"
                  name="performance_target"
                  value={formData.performance_target}
                  onChange={handleChange}
                  step="0.01"
                  max="999.99"
                  readOnly={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Annual Goals</label>
                <textarea
                  name="annual_goals"
                  value={formData.annual_goals}
                  onChange={handleChange}
                  rows={3}
                  readOnly={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    id="is_profit_center"
                    name="is_profit_center"
                    type="checkbox"
                    checked={formData.is_profit_center}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_profit_center" className="ml-2 block text-sm text-gray-900">
                    Profit Center
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    id="is_active"
                    name="is_active"
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                    Division is active
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              readOnly={isReadOnly}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {isReadOnly ? 'Close' : 'Cancel'}
            </button>
            {!isReadOnly && (
              <button
                type="submit"
                disabled={loading}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {loading ? 'Saving...' : mode === 'create' ? 'Create Division' : 'Update Division'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default DivisionManagement;